import {
	Badge,
	Card,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@ancatag/at-design";
import { Image, MessageSquare, Video, Volume2 } from "lucide-react";
import { cn } from "../../../lib/utils";
import type { GenerationType } from "../../../services/provider-templates";

interface Step1GenerationTypeProps {
	selectedType: GenerationType | null;
	onSelect: (type: GenerationType) => void;
}

const generationTypes: Array<{
	type: GenerationType;
	label: string;
	description: string;
	icon: typeof MessageSquare;
	available: boolean;
}> = [
	{
		type: "text",
		label: "Text Generation",
		description: "Chat completions, text generation, and language tasks",
		icon: MessageSquare,
		available: true,
	},
	{
		type: "image",
		label: "Image Generation",
		description: "Generate images from text prompts",
		icon: Image,
		available: false,
	},
	{
		type: "video",
		label: "Video Generation",
		description: "Generate videos from text or images",
		icon: Video,
		available: false,
	},
	{
		type: "audio",
		label: "Audio Generation",
		description: "Text-to-speech and audio generation",
		icon: Volume2,
		available: false,
	},
];

export function Step1GenerationType({
	selectedType,
	onSelect,
}: Step1GenerationTypeProps) {
	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground mb-6">
				Select the type of generation this model configuration will handle. For
				now, only text generation is available.
			</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{generationTypes.map((gt) => {
					const Icon = gt.icon;
					const isSelected = selectedType === gt.type;
					const isDisabled = !gt.available;

					return (
						<Card
							key={gt.type}
							className={cn(
								"cursor-pointer transition-all hover:shadow-md min-h-[120px] flex flex-col justify-center",
								isSelected && "ring-2 ring-primary",
								isDisabled && "opacity-50 cursor-not-allowed",
							)}
							onClick={() => !isDisabled && onSelect(gt.type)}
						>
							<CardHeader>
								<div className="flex items-start justify-between">
									<div className="flex items-center gap-3">
										<div
											className={cn(
												"p-2 rounded-lg",
												isSelected
													? "bg-primary text-primary-foreground"
													: "bg-muted",
											)}
										>
											<Icon className="h-5 w-5" />
										</div>
										<div>
											<CardTitle className="text-lg">{gt.label}</CardTitle>
											<CardDescription className="mt-1">
												{gt.description}
											</CardDescription>
										</div>
									</div>
									{isDisabled && <Badge variant="secondary">Coming Soon</Badge>}
								</div>
							</CardHeader>
						</Card>
					);
				})}
			</div>
		</div>
	);
}
